import React from 'react';
import { CanvasElement } from './types';
import ReactBarcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';

interface PrintableLabelProps {
  elements: CanvasElement[];
  widthMm: number;
  heightMm: number;
  data?: Record<string, any>;
}

export const PrintableLabel: React.FC<PrintableLabelProps> = ({
  elements,
  widthMm,
  heightMm,
  data
}) => {

  const replaceVariables = (text: string) => {
    if (!data || !text) return text;
    let result = text;
    
    let dataToUse = data.data && typeof data.data === 'object' ? data.data : data;
    if (Array.isArray(dataToUse)) {
      dataToUse = dataToUse[0] || {};
    }

    const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
      if (!obj) return {};
      return Object.keys(obj).reduce((acc: Record<string, any>, k: string) => {
        const pre = prefix.length ? prefix + '.' : '';
        if (typeof obj[k] === 'object' && obj[k] !== null && !Array.isArray(obj[k])) {
          Object.assign(acc, flattenObject(obj[k], pre + k));
        } else {
          acc[pre + k] = obj[k];
        }
        return acc;
      }, {});
    };

    const flatData = flattenObject(dataToUse);

    for (const key of Object.keys(flatData)) {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'gi');
      result = result.replace(regex, String(flatData[key] || ""));
    }

    const now = new Date();
    result = result.replace(/{{\s*TARIH\s*}}/gi, now.toLocaleDateString('tr-TR'));
    result = result.replace(/{{\s*SAAT\s*}}/gi, now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));

    return result;
  };

  const renderElementContent = (element: CanvasElement, contentW?: number, contentH?: number) => {
    const displayContent = replaceVariables(element.content);

    switch (element.type) {
      case 'TEXT':
        return (
          <div 
            style={{ 
              fontSize: `${element.fontSize || 12}pt`,
              fontWeight: element.fontWeight || 'normal',
              fontFamily: 'Arial, Helvetica, sans-serif',
              width: '100%',
              height: '100%',
              display: (element.maxLines && element.maxLines > 1) ? '-webkit-box' : 'flex',
              WebkitBoxOrient: (element.maxLines && element.maxLines > 1) ? 'vertical' : undefined,
              WebkitLineClamp: (element.maxLines && element.maxLines > 1) ? element.maxLines : undefined,
              alignItems: (element.maxLines && element.maxLines > 1) ? undefined : 'center',
              justifyContent: (element.maxLines && element.maxLines > 1) ? undefined : (element.textAlign === 'center' ? 'center' : (element.textAlign === 'right' ? 'flex-end' : 'flex-start')),
              textAlign: element.textAlign as any || 'left',
              wordBreak: 'break-word',
              lineHeight: 1.2,
              color: '#000000',
              borderWidth: element.borderWidth ? `${element.borderWidth}px` : '0px',
              borderStyle: element.borderStyle || 'solid',
              borderColor: '#000000',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}
          >
            {displayContent}
          </div>
        );
      case 'BARCODE':
        const barcodeH = (contentH ? contentH * 3.78 * 0.75 : (element.height ? element.height * 3.78 * 0.75 : 35));
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
             {displayContent ? (
               <ReactBarcode 
                 value={displayContent} 
                 width={1.4}
                 height={barcodeH}
                 fontSize={11}
                 displayValue={element.showBarcodeText !== false}
                 margin={0}
                 background="transparent"
               />
             ) : (
               <div style={{ fontSize: '10px' }}>Örnek Barkod</div>
             )}
          </div>
        );
      case 'QR':
        return (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1px' }}>
            {displayContent ? (
              <QRCodeSVG 
                value={displayContent} 
                size={element.width ? element.width * 3.78 - 4 : 40} 
                level="L"
              />
            ) : (
              <div style={{ fontSize: '8px' }}>Örnek QR</div>
            )}
          </div>
        );
      case 'BOX':
        return (
          <div 
            style={{ 
              width: '100%', 
              height: '100%', 
              borderWidth: `${element.borderWidth || 1}px`, 
              borderStyle: element.borderStyle || 'solid', 
              borderColor: '#000000',
              boxSizing: 'border-box'
            }} 
          />
        );
      case 'LINE':
        return (
          <div 
            style={{ 
              width: '100%', 
              height: 0,
              borderTopWidth: `${element.borderWidth || 1}px`, 
              borderTopStyle: element.borderStyle || 'solid', 
              borderColor: '#000000',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)'
            }} 
          />
        );
      case 'VLINE':
        return (
          <div 
            style={{ 
              height: '100%', 
              width: 0,
              borderLeftWidth: `${element.borderWidth || 1}px`, 
              borderLeftStyle: element.borderStyle || 'solid', 
              borderColor: '#000000',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)'
            }} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className="printable-label"
      style={{ 
        width: `${widthMm}mm`, 
        height: `${heightMm}mm`,
        position: 'relative',
        backgroundColor: '#fff',
        color: '#000',
        pageBreakAfter: 'always',
        overflow: 'hidden'
      }}
    >
      {elements.map((el) => {
        const isOrthogonal = el.rotation === 90 || el.rotation === 270;
        const outerW = el.width || 10;
        const outerH = el.height || 10;
        const innerW = isOrthogonal ? outerH : outerW;
        const innerH = isOrthogonal ? outerW : outerH;

        return (
          <div
            key={el.id}
            style={{
              position: 'absolute',
              left: `${el.x}mm`,
              top: `${el.y}mm`,
              width: `${outerW}mm`,
              height: `${outerH}mm`,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${innerW}mm`,
                height: `${innerH}mm`,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) ${el.rotation ? `rotate(${el.rotation}deg)` : ''}`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderElementContent(el, innerW, innerH)}
            </div>
          </div>
        );
      })}
    </div>
  );
};
